import React, {useRef, useState} from 'react';
import * as Sentry from '@sentry/react';
import {useIsTouch} from '../hooks/useIsTouch';

export const BugReportButton: React.FC = () => {
  const isTouch = useIsTouch();
  const [hovered, setHovered] = useState(false);
  const [touchExpanded, setTouchExpanded] = useState(false);
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expanded = isTouch ? touchExpanded : hovered;

  const handleMouseEnter = () => {
    if (isTouch) return;
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
      collapseTimer.current = null;
    }
    setHovered(true);
  };

  const handleMouseLeave = () => {
    if (isTouch) return;
    collapseTimer.current = setTimeout(() => setHovered(false), 300);
  };

  const fireAction = async () => {
    const feedback = Sentry.getFeedback();
    const form = await feedback?.createForm();
    if (form) {
      form.appendToDom();
      form.open();
    }
  };

  const handleIconClick = (e: React.MouseEvent) => {
    if (isTouch) {
      e.stopPropagation();
      setTouchExpanded(prev => !prev);
    }
  };

  // Mouse: whole container fires action. Touch: only the label area fires action.
  const handleContainerClick = () => {
    if (!isTouch) void fireAction();
  };

  const handleLabelClick = (e: React.MouseEvent) => {
    if (isTouch && touchExpanded) {
      e.stopPropagation();
      setTouchExpanded(false);
      void fireAction();
    }
  };

  return (
    <div
      onClick={handleContainerClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'fixed',
        top: 52,
        right: 12,
        zIndex: 6000,
        background: expanded ? 'rgba(18, 40, 64, 0.9)' : 'rgba(10, 10, 20, 0.75)',
        border: '1px solid #5aa0ff',
        borderRadius: 20,
        padding: '5px 8px',
        fontFamily: 'monospace',
        fontSize: 12,
        color: '#a8d1ff',
        backdropFilter: 'blur(4px)',
        cursor: 'pointer',
        pointerEvents: 'auto',
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        transition: 'background 0.2s ease-in-out',
        userSelect: 'none',
      }}
    >
      <span onClick={handleIconClick}>📢</span>
      <span
        onClick={handleLabelClick}
        style={{
          maxWidth: expanded ? 120 : 0,
          overflow: 'hidden',
          opacity: expanded ? 1 : 0,
          marginLeft: expanded ? 6 : 0,
          transition: 'max-width 0.2s ease-in-out, opacity 0.2s ease-in-out, margin-left 0.2s ease-in-out',
        }}
      >
        Report a Bug
      </span>
    </div>
  );
};
